import { getToken } from "firebase/app-check";
import { getIdToken, getAppCheckInstance } from "./firebase";
import type { CompressedImage } from "./image";
import type { ScanResponse, SubmitRequest, SubmitResponse } from "./types";
import { ApiError } from "./apiError";
import { buildScanForm } from "./scanForm";
import { resolveAppCheckToken } from "./appCheck";

export { ApiError } from "./apiError";

async function buildHeaders(): Promise<Record<string, string>> {
  const idToken = await getIdToken();
  // Always send the App Check header so the server can rely on its presence.
  // Real token in prod; "dev" placeholder in local emulator (the Function skips
  // verification when FUNCTIONS_EMULATOR is set).
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
    "X-Firebase-AppCheck": "dev",
  };
  const appCheck = getAppCheckInstance();
  if (appCheck) {
    // A failed or hanging attestation must not take the request down before it
    // is even sent: that failure mode left no trace on the server and nothing
    // actionable on screen. A rejected token comes back as a logged 401.
    headers["X-Firebase-AppCheck"] = await resolveAppCheckToken(() => getToken(appCheck, false));
  }
  return headers;
}

export async function apiScan(images: CompressedImage[]): Promise<ScanResponse> {
  const headers = await buildHeaders();
  const res = await fetch("/api/scan", { method: "POST", body: buildScanForm(images), headers });
  if (!res.ok) throw await asApiError(res);
  return res.json();
}

export async function apiSubmit(body: SubmitRequest): Promise<SubmitResponse> {
  const headers = { ...(await buildHeaders()), "Content-Type": "application/json" };
  const res = await fetch("/api/submit", { method: "POST", body: JSON.stringify(body), headers });
  if (!res.ok) throw await asApiError(res);
  return res.json();
}

export interface UidLookupResult {
  uid: string | null;
  confidence: "high" | "medium" | "low";
}

export async function apiLookupUid(merchant: string, hint?: string): Promise<UidLookupResult> {
  const headers = { ...(await buildHeaders()), "Content-Type": "application/json" };
  const res = await fetch("/api/lookup-uid", {
    method: "POST",
    body: JSON.stringify({ merchant, hint }),
    headers,
  });
  if (!res.ok) throw await asApiError(res);
  return res.json();
}

export type ReportKind = "process_file" | "network" | "server" | "unknown" | "wrong_result";

export interface ScanReport {
  kind: ReportKind;
  detail?: string;
  /** What was being uploaded. Type and size only, never the file itself. */
  pages?: { mimeType: string; bytes: number }[];
  scan?: { total: number; itemCount: number; confidence: string };
}

/**
 * Tell the server about a failure it cannot see: one that happened in the
 * browser, or a result that looked fine to the server and was wrong.
 *
 * Fire-and-forget by design. Reporting a failure must never produce a second
 * failure the user has to care about, so every error here is swallowed.
 */
export async function apiReport(body: ScanReport): Promise<void> {
  try {
    const headers = { ...(await buildHeaders()), "Content-Type": "application/json" };
    await fetch("/api/report", { method: "POST", body: JSON.stringify(body), headers });
  } catch (err) {
    console.error("[report] could not be sent", err);
  }
}

async function asApiError(res: Response): Promise<ApiError> {
  let detail = "";
  let retryAfter: number | undefined;
  try {
    const json = await res.json();
    detail = json.error ?? "";
    retryAfter = json.retryAfter;
  } catch { /* ignore */ }
  return new ApiError(detail || `HTTP ${res.status}`, res.status, { retryAfter });
}
